import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";

const BOOKMARKLET = `javascript:(()=>{const rows=[...document.querySelectorAll("[data-itemid], [id^='item_']")];const items=rows.map(row=>{const text=(row.innerText||"").toLowerCase();const a=row.querySelector("a[id^='itemName_'], a[href*='/dp/']");const img=row.querySelector("img");const price=row.querySelector(".a-price .a-offscreen,[id^='itemPrice_']");const title=(a?.innerText||row.querySelector("[id^='itemName_']")?.innerText||"").trim();const productUrl=(a?.href||"").split("?")[0];const imageUrl=img?.src||img?.getAttribute("data-src")||"";const estimatedCost=price?Number(price.innerText.replace(/[^0-9.]/g,"")):null;const purchased=text.includes("purchased")||text.includes("already purchased")||text.includes("last purchased")||text.includes("fulfilled")||text.includes("0 of")||text.includes("needs 0")||text.includes("0 needs");return{title,productUrl,imageUrl,estimatedCost,purchased};}).filter(x=>x.title&&x.productUrl);navigator.clipboard.writeText(JSON.stringify(items,null,2)).then(()=>alert("Copied "+items.length+" wishlist items. Paste into SomebodyGives admin."));})();`;

export default function ImportWishlist() {
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState("");
  const [wishlistJson, setWishlistJson] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedDrafts, setSelectedDrafts] = useState(new Set());

  async function loadOrganizations() {
    const snap = await getDocs(collection(db, "organizations"));
    const rows = snap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => (a.orgName || "").localeCompare(b.orgName || ""));

    setOrganizations(rows);
  }

  async function loadDrafts() {
    const q = query(
      collection(db, "importedNeeds"),
      where("status", "==", "draft"),
      orderBy("createdAt", "desc")
    );

    const snap = await getDocs(q);
    setDrafts(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    setSelectedDrafts(new Set());
  }

  useEffect(() => {
    loadOrganizations();
    loadDrafts();
  }, []);

  async function handleJsonImport() {
    if (!organizationId || !wishlistJson.trim()) {
      setMessage("Choose an organization and paste extracted wishlist JSON.");
      return;
    }

    let parsed;

    try {
      parsed = JSON.parse(wishlistJson);
    } catch (err) {
      setMessage("Invalid JSON. Re-copy from the Amazon wishlist bookmarklet.");
      return;
    }

    if (!Array.isArray(parsed)) {
      setMessage("The pasted JSON must be an array of wishlist items.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const fn = httpsCallable(functions, "importAmazonWishlistJson");
      const result = await fn({
        organizationId,
        items: parsed,
      });

      setMessage(`Imported ${result.data.count} unpurchased draft item(s).`);
      setWishlistJson("");
      await loadDrafts();
    } catch (err) {
      console.error(err);
      setMessage(err.message || "JSON import failed.");
    } finally {
      setLoading(false);
    }
  }

  async function approveDraft(importId) {
    setLoading(true);
    setMessage("");

    try {
      const fn = httpsCallable(functions, "approveImportedNeed");
      await fn({ importId });
      await loadDrafts();
      setMessage("Draft approved and converted to a need.");
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Approval failed.");
    } finally {
      setLoading(false);
    }
  }

  async function rejectDraft(importId) {
    setLoading(true);
    setMessage("");

    try {
      const fn = httpsCallable(functions, "rejectImportedNeed");
      await fn({ importId });
      await loadDrafts();
      setMessage("Draft rejected.");
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Reject failed.");
    } finally {
      setLoading(false);
    }
  }

  async function approveSelected() {
    if (selectedDrafts.size === 0) return;

    if (!window.confirm(`Approve ${selectedDrafts.size} selected draft item(s)?`)) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const fn = httpsCallable(functions, "approveImportedNeed");

      for (const importId of selectedDrafts) {
        await fn({ importId });
      }

      await loadDrafts();
      setMessage(`Approved ${selectedDrafts.size} selected draft item(s).`);
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Bulk approval failed.");
    } finally {
      setLoading(false);
    }
  }

  async function rejectSelected() {
    if (selectedDrafts.size === 0) return;

    if (!window.confirm(`Reject ${selectedDrafts.size} selected draft item(s)?`)) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const fn = httpsCallable(functions, "rejectImportedNeed");

      for (const importId of selectedDrafts) {
        await fn({ importId });
      }

      await loadDrafts();
      setMessage(`Rejected ${selectedDrafts.size} selected draft item(s).`);
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Bulk reject failed.");
    } finally {
      setLoading(false);
    }
  }

  function toggleSelected(importId) {
    setSelectedDrafts((prev) => {
      const next = new Set(prev);

      if (next.has(importId)) {
        next.delete(importId);
      } else {
        next.add(importId);
      }

      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedDrafts.size === drafts.length) {
      setSelectedDrafts(new Set());
    } else {
      setSelectedDrafts(new Set(drafts.map((d) => d.id)));
    }
  }

  async function copyBookmarklet() {
    try {
      await navigator.clipboard.writeText(BOOKMARKLET);
      setMessage("Bookmarklet copied. Create a browser bookmark and paste this into the URL field.");
    } catch (err) {
      setMessage("Could not copy bookmarklet. Select and copy it manually.");
    }
  }

  return (
    <div className="admin-section">
      <h2>Import Amazon Wish List</h2>

      <div className="admin-card" style={{ marginBottom: "1rem" }}>
        <h3>Step 1: Install bookmarklet</h3>

        <p style={{ fontSize: "0.9rem", color: "#555", lineHeight: 1.5 }}>
          Create a browser bookmark named <strong>Copy Amazon Wishlist</strong>. Edit the bookmark and paste this code into the URL field.
        </p>

        <button onClick={copyBookmarklet} disabled={loading}>
          Copy Bookmarklet Code
        </button>

        <textarea
          readOnly
          value={BOOKMARKLET}
          style={{
            width: "100%",
            minHeight: "90px",
            marginTop: "0.75rem",
            fontSize: "0.75rem",
            fontFamily: "monospace",
          }}
        />

        <h3 style={{ marginTop: "1.5rem" }}>Step 2: Copy wishlist items from Amazon</h3>

        <ol style={{ fontSize: "0.9rem", color: "#555", lineHeight: 1.6 }}>
          <li>Open the Amazon wishlist in your browser.</li>
          <li>Scroll until all items are loaded.</li>
          <li>Click your <strong>Copy Amazon Wishlist</strong> bookmarklet.</li>
          <li>Return here and paste the copied JSON below.</li>
        </ol>

        <h3 style={{ marginTop: "1.5rem" }}>Step 3: Import as drafts</h3>

        <div style={{ marginBottom: "0.75rem" }}>
          <label>Organization</label>
          <select
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            disabled={loading}
            style={{ marginLeft: "0.5rem" }}
          >
            <option value="">Select organization</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.orgName || org.id}
              </option>
            ))}
          </select>
        </div>

        <textarea
          value={wishlistJson}
          onChange={(e) => setWishlistJson(e.target.value)}
          placeholder="Paste JSON copied by the Amazon wishlist bookmarklet..."
          disabled={loading}
          style={{
            width: "100%",
            minHeight: "180px",
            fontFamily: "monospace",
            fontSize: "0.8rem",
          }}
        />

        <div style={{ marginTop: "0.75rem" }}>
          <button onClick={handleJsonImport} disabled={loading}>
            {loading ? "Working..." : "Import Extracted Wishlist Items"}
          </button>
        </div>

        {message && (
          <p style={{ marginTop: "0.75rem", fontWeight: 600 }}>{message}</p>
        )}
      </div>

      <h3>Draft Imported Needs ({drafts.length})</h3>

      {drafts.length === 0 && <p>No draft imported needs.</p>}

      {drafts.length > 0 && (
        <div
          className="admin-card"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              type="checkbox"
              checked={selectedDrafts.size === drafts.length}
              onChange={toggleSelectAll}
              disabled={loading}
            />
            Select all drafts
          </label>

          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              onClick={approveSelected}
              disabled={loading || selectedDrafts.size === 0}
            >
              Approve selected ({selectedDrafts.size})
            </button>

            <button
              onClick={rejectSelected}
              disabled={loading || selectedDrafts.size === 0}
            >
              Reject selected ({selectedDrafts.size})
            </button>
          </div>
        </div>
      )}

      <div className="imported-needs-list">
        {drafts.map((item) => {
          const selected = selectedDrafts.has(item.id);

          return (
            <div
              key={item.id}
              className="admin-card imported-need-card"
              style={{
                display: "grid",
                gridTemplateColumns: "32px 90px 1fr auto",
                gap: "1rem",
                alignItems: "center",
                marginBottom: "0.75rem",
                border: selected ? "2px solid #1e3a5f" : "1px solid #eee",
                borderRadius: "10px",
                padding: "1rem",
                background: selected ? "#f0f4ff" : "white",
              }}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => toggleSelected(item.id)}
                disabled={loading}
              />

              <div>
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    style={{
                      width: 80,
                      height: 80,
                      objectFit: "contain",
                      border: "1px solid #eee",
                      borderRadius: "8px",
                      background: "white",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      border: "1px solid #eee",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                      fontSize: "0.8rem",
                    }}
                  >
                    No image
                  </div>
                )}
              </div>

              <div>
                <h4 style={{ margin: "0 0 0.35rem" }}>{item.title}</h4>

                <div style={{ fontSize: "0.85rem", color: "#666" }}>
                  {item.organizationName}
                </div>

                <div style={{ fontSize: "0.85rem", color: "#666" }}>
                  Estimated cost:{" "}
                  {item.estimatedCost
                    ? `$${Number(item.estimatedCost).toFixed(2)}`
                    : "Unknown"}
                </div>

                {item.productUrl && (
                  <a href={item.productUrl} target="_blank" rel="noreferrer">
                    View product
                  </a>
                )}
              </div>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button onClick={() => approveDraft(item.id)} disabled={loading}>
                  Approve
                </button>

                <button onClick={() => rejectDraft(item.id)} disabled={loading}>
                  Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}